import React, { memo, Fragment, useMemo, useEffect, useCallback } from "react";
import pluralize from "pluralize";

import { useAxiomState } from "__AxiomComponents/AxiomHooks/AxiomHooks";

import Icon from "../Icons/Icon";
import HawkeyeV2AddPost from "../HawkeyeV2AddPost/HawkeyeV2AddPost";
import HawkeyeV2Posts from "../HawkeyeV2Posts/HawkeyeV2Posts";

import "./HawkeyeCommunication.scss";

function HawkeyeCommunication(props) {
  const { widget } = props;

  const [state, dispatch] = useAxiomState(
    {
      loading: true,
      collaborators: [],
      conversations: [],
      isAddingPost: false,
      isEditingPost: false,
      isExpanded: false,
      postName: "",
      postDescription: "",
      selectedCollaborators: [],
      isAllCollaboratorsSelected: false,
      assignedCollaborator: null,
      dueDate: "",
    },
    props.appName,
    props.appID,
    "HawkeyeCommunication",
    props.componentID
  );

  useEffect(
    () =>
      dispatch(
        {
          projectID: widget.projectID,
          subscribeID: widget.subscribeID,
          shouldRestrictCommToSameUserProfile: widget.shouldRestrictCommToSameUserProfile,
        },
        "AxiomEvent",
        "getConversations"
      ),
    [dispatch, widget.projectID, widget.subscribeID, widget.shouldRestrictCommToSameUserProfile]
  );

  const toggleAddPost = useCallback(() => {
    dispatch({
      isAddingPost: !state.isAddingPost,
      postName: "",
      postDescription: "",
      selectedCollaborators: [],
      isAllCollaboratorsSelected: false,
      assignedCollaborator: null,
      dueDate: "",
      emailSettings: state.defaultEmailSettings,
    });
  }, [dispatch, state.isAddingPost, state.defaultEmailSettings]);

  const toggleEditPost = useCallback(
    () => dispatch((state) => ({ isEditingPost: !state.isEditingPost })),
    [dispatch]
  );

  const onPostAdd = useCallback(
    (
      postName,
      postDescription,
      collaborators,
      assignedCollaborator,
      dueDate,
      emailConfiguration
    ) => {
      toggleAddPost();
      dispatch({ loading: true });
      dispatch(
        {
          projectID: widget.projectID,
          name: postName,
          description: postDescription,
          collaborators: collaborators,
          assignedCollaborator: assignedCollaborator,
          isPublic: state.isAllCollaboratorsSelected,
          dueDate: dueDate,
          emailConfiguration: emailConfiguration,
          dashboardName: widget.pagename,
          subscribeID: widget.subscribeID,
        },
        "AxiomEvent",
        "createConversation"
      );
    },
    [
      dispatch,
      widget.projectID,
      widget.pagename,
      widget.subscribeID,
      toggleAddPost,
      state.isAllCollaboratorsSelected,
    ]
  );

  const onPostEdit = useCallback(
    (
      postName,
      postDescription,
      collaborators,
      assignedCollaborator,
      dueDate,
      emailConfiguration
    ) => {
      toggleEditPost();
      dispatch({ loading: true });
      dispatch(
        {
          projectID: widget.projectID,
          postId: state.editPostId,
          name: postName,
          description: postDescription,
          collaborators: collaborators,
          assignedCollaborator: assignedCollaborator,
          isPublic: state.isAllCollaboratorsSelected,
          dueDate: dueDate,
          emailConfiguration: emailConfiguration,
          dashboardName: widget.pagename,
          subscribeID: widget.subscribeID,
        },
        "AxiomEvent",
        "editConversation"
      );
    },
    [
      dispatch,
      widget.projectID,
      widget.pagename,
      widget.subscribeID,
      toggleEditPost,
      state.isAllCollaboratorsSelected,
      state.editPostId,
    ]
  );

  const toggleExpanded = useCallback(
    () =>
      dispatch((state) => ({
        isExpanded: !state.isExpanded,
        ...(state.isExpanded ? { isAddingPost: false } : {}),
      })),
    [dispatch]
  );

  const totalComments = useMemo(() => state.conversations.reduce((a, r) => a + r.comments, 0), [
    state.conversations,
  ]);

  const allCollaborators = useMemo(
    () =>
      state.collaborators.map((c) => ({
        displayName: `${c.firstName ? c.firstName : ""} ${c.lastName ? c.lastName : ""}`,
        key: c.email,
        userID: c.userID,
      })),
    [state.collaborators]
  );

  const onAllCollaboratorsSelection = (e) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      dispatch({
        selectedCollaborators: allCollaborators,
        isAllCollaboratorsSelected: true,
      });
    } else {
      dispatch({
        selectedCollaborators: [],
        isAllCollaboratorsSelected: false,
      });
    }
  };

  const onColaboratorSelection = useCallback(
    (selectedOption) => {
      dispatch((state) => {
        const selectedOptions = [...state.selectedCollaborators];
        const searchIndex = selectedOptions.map((i) => i.key).indexOf(selectedOption.key);
        if (searchIndex > -1) {
          selectedOptions.splice(searchIndex, 1);
          return { selectedCollaborators: selectedOptions };
        } else {
          return { selectedCollaborators: [...selectedOptions, selectedOption] };
        }
      });
    },
    [dispatch]
  );

  const noOfAssignedConversations = useMemo(() => {
    let noOfAssignedConversations = 0;
    state.conversations.forEach((d) => {
      if (d.isAssignedToMe) {
        ++noOfAssignedConversations;
      }
    });
    return noOfAssignedConversations;
  }, [state.conversations]);

  const onPostNameChange = useCallback(
    (value) => {
      dispatch({
        postName: value,
      });
    },
    [dispatch]
  );

  const onPostDescriptionChange = useCallback(
    (value) => {
      dispatch({
        postDescription: value,
      });
    },
    [dispatch]
  );

  const allAssignedCollaborators = useMemo(
    () =>
      state.collaborators.map((c) => ({
        label: `${c.firstName ? c.firstName : ""} ${c.lastName ? c.lastName : ""}`,
        key: c.email,
        value: c.userID,
        userID: c.userID,
      })),
    [state.collaborators]
  );

  const onCollaboratorAssigned = useCallback(
    (key, value) => {
      dispatch({
        assignedCollaborator: value,
      });
    },
    [dispatch]
  );

  const setDueDate = useCallback(
    (value) => {
      dispatch({
        dueDate: value,
      });
    },
    [dispatch]
  );

  const editPost = useCallback(
    (key) => {
      const tobeEditPost = state.conversations.find((d) => d.id === key);
      let selectedCollaborators = [];
      if (tobeEditPost.isPublic) {
        selectedCollaborators = allCollaborators;
      } else {
        selectedCollaborators = tobeEditPost.collaborators.map((d) => ({
          displayName: d.name,
          key: d.email,
          userID: d.UID,
        }));
      }
      dispatch({
        editPostId: key,
        postName: tobeEditPost.name,
        postDescription: tobeEditPost.description,
        assignedCollaborator: tobeEditPost.assignTo,
        dueDate: d3.timeFormat("%Y-%m-%d")(new Date(tobeEditPost.dueDate)),
        isAllCollaboratorsSelected: tobeEditPost.isPublic,
        canEditNameDesc: tobeEditPost.canEditNameDesc,
        selectedCollaborators: selectedCollaborators,
        isEditingPost: true,
        emailSettings: tobeEditPost.emailConfiguration,
      });
    },
    [state.conversations, dispatch, allCollaborators]
  );

  const onMarkAsCompleted = useCallback(
    (postId, sendEmail) => {
      dispatch({ loading: true });
      dispatch(
        {
          postId,
          projectID: widget.projectID,
          sendEmail: sendEmail,
        },
        "AxiomEvent",
        "markAsCompleted"
      );
    },
    [dispatch, widget.projectID]
  );

  return (
    <div>
      <a onClick={toggleExpanded} className="hawkeye-conversations-stats">
        <div className="hawkeye-conversations-stat">
          <Icon icon="ICON_CONVERSATIONS" className="hawkeye-conversations-stat-icon" />
          {state.conversations.length > 0 ? (
            <Fragment>
              <span className="hawkeye-conversations-stat-number">
                {state.conversations.length}
              </span>
              <span>
                {pluralize(AxiomAPIs.translate("conversation"), state.conversations.length)}
              </span>
            </Fragment>
          ) : (
            AxiomAPIs.translate("No conversations")
          )}
        </div>
        <div className="hawkeye-conversations-stat">
          <Icon icon="ICON_COMMENT" className="hawkeye-conversations-stat-icon" />
          {totalComments > 0 ? (
            <Fragment>
              <span className="hawkeye-conversations-stat-number">{totalComments}</span>
              <span>{pluralize(AxiomAPIs.translate("comment"), totalComments)}</span>
            </Fragment>
          ) : (
            AxiomAPIs.translate("No comments")
          )}
        </div>
        <div className="hawkeye-conversations-stat">
          <Icon icon="ICON_COLLABORATORS" className="hawkeye-conversations-stat-icon" />
          {state.collaborators.length > 0 ? (
            <Fragment>
              <span className="hawkeye-conversations-stat-number">
                {state.collaborators.length}
              </span>
              <span>
                {pluralize(AxiomAPIs.translate("collaborator"), state.collaborators.length)}
              </span>
            </Fragment>
          ) : (
            AxiomAPIs.translate("No collaborators")
          )}
        </div>
        <div className="hawkeye-conversations-stat">
          <Icon icon="ICON_CONVERSATIONS" className="hawkeye-conversations-stat-icon" />
          {noOfAssignedConversations > 0
            ? pluralize(AxiomAPIs.translate("Assigned Posts"), noOfAssignedConversations, true)
            : AxiomAPIs.translate("No Assigned Posts")}
        </div>
      </a>
      {state.isExpanded && (
        <Fragment>
          {state.isAddingPost || state.isEditingPost ? (
            <HawkeyeV2AddPost
              onPostAdd={onPostAdd}
              onPostEdit={onPostEdit}
              closeAddPost={toggleAddPost}
              closeEditPost={toggleEditPost}
              postName={state.postName}
              onPostNameChange={onPostNameChange}
              postDescription={state.postDescription}
              onPostDescriptionChange={onPostDescriptionChange}
              onAllCollaboratorsSelection={onAllCollaboratorsSelection}
              onColaboratorSelection={onColaboratorSelection}
              selectedCollaborators={state.selectedCollaborators}
              isAllCollaboratorsSelected={state.isAllCollaboratorsSelected}
              allCollaborators={allCollaborators}
              allAssignedCollaborators={allAssignedCollaborators}
              assignedCollaborator={state.assignedCollaborator}
              onCollaboratorAssigned={onCollaboratorAssigned}
              dueDate={state.dueDate}
              setDueDate={setDueDate}
              editing={state.isEditingPost}
              canEditNameDesc={state.canEditNameDesc}
              emailSettings={state.emailSettings}
            />
          ) : (
            <HawkeyeV2Posts
              widget={widget}
              isLoading={state.loading}
              projectID={widget.projectID}
              onAddPost={toggleAddPost}
              onEditPost={editPost}
              conversations={state.conversations}
              allCollaborators={allAssignedCollaborators}
              appName={props.appName}
              appID={props.appID}
              componentID={props.componentID}
              onMarkAsCompleted={onMarkAsCompleted}
            />
          )}
        </Fragment>
      )}
    </div>
  );
}

export default memo(HawkeyeCommunication);
